import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchRecords, deleteRecord } from '../recordsApi';
import { useAsync } from '../hooks';
import { EditIcon, TrashIcon } from '../components/Icons';

const formatPrice = (value) => {
  const number = Number(value);
  return value != null && value !== '' && !Number.isNaN(number) ? `€${number.toLocaleString()}` : value;
};

const toNumber = (value) => {
  const number = parseFloat(String(value ?? '').replace(/[€,]/g, ''));
  return Number.isNaN(number) ? null : number;
};

// type: 'text' compares case-insensitively, 'number' compares numerically (dates are ISO strings, so text works)
const COLUMNS = [
  { key: 'Address__c', label: 'Address', type: 'text' },
  { key: 'Asking_Price__c', label: 'Price', type: 'number' },
  { key: 'Beds__c', label: 'Beds', type: 'number' },
  { key: 'Baths__c', label: 'Baths', type: 'number' },
  { key: 'sqm__c', label: 'Floor Area (m²)', type: 'number' },
  { key: 'BER_Rating__c', label: 'BER', type: 'text' },
  { key: 'Eircode', label: 'Eircode', type: 'text' },
  { key: 'Date_Listed__c', label: 'Listed', type: 'text' },
  { key: 'Location__Latitude__s', label: 'Latitude', type: 'number' },
  { key: 'Location__Longitude__s', label: 'Longitude', type: 'number' }
];

const SEARCH_FIELDS = ['Address__c', 'Eircode', 'Asking_Price__c', 'Price__c', 'BER_Rating__c', 'Broker__c', 'DaftId__c'];

const matchesSearch = (record, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const compact = q.replace(/\s+/g, '');
  // Also match the price as displayed (e.g. "€3,450,000")
  if (String(formatPrice(record.Asking_Price__c) ?? '').toLowerCase().includes(q)) return true;
  return SEARCH_FIELDS.some((f) => {
    const value = String(record[f] ?? '').toLowerCase();
    return value.includes(q) || (f === 'Eircode' && value.includes(compact));
  });
};

const compareRecords = (column, direction) => (a, b) => {
  const av = column.type === 'number' ? toNumber(a[column.key]) : String(a[column.key] ?? '').toLowerCase();
  const bv = column.type === 'number' ? toNumber(b[column.key]) : String(b[column.key] ?? '').toLowerCase();
  const aEmpty = av === null || av === '';
  const bEmpty = bv === null || bv === '';
  // Blanks (and non-numeric prices such as POA) count as the smallest value
  let result;
  if (aEmpty || bEmpty) result = aEmpty === bEmpty ? 0 : aEmpty ? -1 : 1;
  else result = column.type === 'number' ? av - bv : av.localeCompare(bv);
  return direction === 'asc' ? result : -result;
};

export default function RecordsList() {
  const { data: loaded, loading, error } = useAsync(fetchRecords, []);
  const [deletedIds, setDeletedIds] = useState(() => new Set());
  const [deleteError, setDeleteError] = useState(null);
  const records = useMemo(() => loaded && loaded.filter((r) => !deletedIds.has(r.DaftId__c)), [loaded, deletedIds]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(null); // { key, direction }

  const visible = useMemo(() => {
    if (!records) return [];
    const filtered = records.filter((r) => matchesSearch(r, search));
    if (!sort) return filtered;
    const column = COLUMNS.find((c) => c.key === sort.key);
    return [...filtered].sort(compareRecords(column, sort.direction));
  }, [records, search, sort]);

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete this listing?\n\n${record.Address__c}\n\nThis cannot be undone.`)) return;
    setDeleteError(null);
    try {
      await deleteRecord(record.DaftId__c);
      setDeletedIds((ids) => new Set(ids).add(record.DaftId__c));
    } catch (err) {
      setDeleteError(`Could not delete ${record.Address__c}: ${err.message}`);
    }
  };

  const toggleSort = (key) =>
    setSort((current) =>
      current && current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );

  return (
    <div className="rec rec-page rec-wide">
      <Link to="/" className="rec-btn rec-back">
        <span aria-hidden="true">←</span> View map
      </Link>

      <div className="rec-header rec-header-center">
        <h1>Listings</h1>
        <div className="rec-header-right">
          {records && (
            <span className="rec-muted rec-sm">
              {visible.length === records.length ? `${records.length} records` : `${visible.length} of ${records.length} records`}
            </span>
          )}
          <Link to="/duplicates" className="rec-btn">Duplicates</Link>
          <Link to="/pending" className="rec-btn">No-Eircode sales</Link>
          <Link to="/import" className="rec-btn">Import</Link>
          <Link to="/listings/new" className="rec-btn-primary">New listing</Link>
        </div>
      </div>

      <input
        type="search"
        className="rec-search"
        placeholder="Search address, Eircode, price, BER, broker…"
        aria-label="Search listings"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading && <p className="rec-muted rec-sm">Loading…</p>}
      {error && <p className="rec-error rec-sm">Failed to load listings: {error.message}</p>}
      {deleteError && <p className="rec-error rec-sm rec-banner">{deleteError}</p>}

      {records && (
        <div className="rec-table-wrap">
          <table className="rec-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => {
                  const active = sort && sort.key === c.key;
                  return (
                    <th key={c.key} aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button type="button" className="rec-sort" onClick={() => toggleSort(c.key)}>
                        {c.label}
                        <span className="rec-sort-icon" aria-hidden="true">
                          {active ? (sort.direction === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    </th>
                  );
                })}
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map((l) => (
                <tr key={l.DaftId__c}>
                  <td>
                    <Link to={`/listings/${encodeURIComponent(l.DaftId__c)}`} className="rec-row-link">
                      {l.Address__c}
                    </Link>
                  </td>
                  <td>{formatPrice(l.Asking_Price__c)}</td>
                  <td>{l.Beds__c}</td>
                  <td>{l.Baths__c}</td>
                  <td>{l.sqm__c}</td>
                  <td>{l.BER_Rating__c}</td>
                  <td>{l.Eircode}</td>
                  <td className="rec-muted">{l.Date_Listed__c?.slice(0, 10)}</td>
                  <td>{l.Location__Latitude__s}</td>
                  <td>{l.Location__Longitude__s}</td>
                  <td className="rec-actions">
                    <Link to={`/listings/${encodeURIComponent(l.DaftId__c)}/edit`} className="rec-icon-btn" title="Edit" aria-label={`Edit ${l.Address__c}`}>
                      <EditIcon />
                    </Link>
                    <button type="button" className="rec-icon-btn rec-icon-danger" title="Delete" aria-label={`Delete ${l.Address__c}`} onClick={() => handleDelete(l)}>
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className="rec-muted rec-empty">No listings match “{search}”.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
